import type {
  FeatureServiceBinder,
  FeatureServiceProviderDefinition,
  SharedFeatureService,
} from '@feature-hub/core';

export interface PubSubServiceV1 {
  readonly publish: (topic: PubSubTopicV1, payload?: any) => void;

  readonly subscribe: (
    topic: PubSubTopicV1,
    listener: () => void,
    options?: {readonly signal?: AbortSignal},
  ) => () => void;
}

export interface PubSubServiceV2 {
  readonly publish: <T, K extends keyof T & string>(
    topic: K,
    payload?: T[K],
  ) => void;

  readonly subscribe: <T, K extends keyof T & string>(
    topic: K,
    listener: (payload?: T[K]) => void | Promise<void>,
    options?: {readonly signal?: AbortSignal},
  ) => () => void;
}

export type PubSubTopicV1 =
  | 'toggle-comparison-modal'
  | 'cart-update'
  | 'garage-update'
  | 'before-login'
  | 'show-subscribe-popup'
  | 'show-newsletter-popup'
  | 'test-drive-model-update'
  | 'add-to-mini-cart'
  | 'show-campaign-popup';

export interface SharedPubSubService extends SharedFeatureService {
  readonly '1.0.0': FeatureServiceBinder<PubSubServiceV1>;
  readonly '2.0.0': FeatureServiceBinder<PubSubServiceV2>;
}

export const pubSubServiceDefinition: FeatureServiceProviderDefinition<SharedPubSubService> =
  {
    id: `pub-sub`,

    create: () => {
      const listenersByTopic = new Map<string, Set<(options?: any) => void>>();

      return {
        '1.0.0': () => ({
          featureService: {
            publish: (topic, payload) => {
              const listeners = listenersByTopic.get(topic);

              if (listeners) {
                for (const listener of listeners) {
                  listener(payload);
                }
              }
            },

            subscribe: (topic, listener, options = {}) => {
              const listeners = listenersByTopic.get(topic) ?? new Set();

              listenersByTopic.set(topic, listeners);
              listeners.add(listener);

              const unsubscribe = () => listeners.delete(listener);

              options.signal?.addEventListener(`abort`, unsubscribe);

              return unsubscribe;
            },
          },
        }),
        '2.0.0': () => ({
          featureService: {
            publish: (topic, payload) => {
              const listeners = listenersByTopic.get(topic);

              if (listeners) {
                for (const listener of listeners) {
                  listener(payload);
                }
              }
            },

            subscribe: (topic, listener, options = {}) => {
              const listeners = listenersByTopic.get(topic) ?? new Set();

              listenersByTopic.set(topic, listeners);
              listeners.add(listener);

              const unsubscribe = () => listeners.delete(listener);

              options.signal?.addEventListener(`abort`, unsubscribe);

              return unsubscribe;
            },
          },
        }),
      };
    },
  };
